import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquareText } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { fetchPest, formatApiError } from "@/lib/api";

export default function PestDetail() {
    const { slug } = useParams();
    const [pest, setPest] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPest(slug).then(setPest).catch((e) => setError(formatApiError(e)));
    }, [slug]);

    if (error) {
        return (
            <div className="space-y-4" data-testid="pest-detail-error">
                <Button asChild variant="ghost" className="rounded-full">
                    <Link to="/pests"><ArrowLeft className="w-4 h-4 mr-2" /> Back to pests</Link>
                </Button>
                <p className="text-[var(--vm-text-muted)]">{error}</p>
            </div>
        );
    }

    if (!pest) {
        return <p className="text-[var(--vm-text-muted)]" data-testid="pest-detail-loading">Loading…</p>;
    }

    const chatPrompt = encodeURIComponent(
        `I've got ${pest.name.toLowerCase()} on my crops. Can you give me tailored advice for my garden?`
    );

    return (
        <div className="space-y-10 max-w-3xl" data-testid={`pest-detail-${pest.slug}`}>
            <Button asChild variant="ghost" className="rounded-full -ml-2">
                <Link to="/pests"><ArrowLeft className="w-4 h-4 mr-2" /> Back to pests</Link>
            </Button>

            {pest.photo?.url && (
                <div
                    className="journal-card overflow-hidden"
                    style={{ background: pest.photo.avg_color }}
                    data-testid="pest-hero-photo"
                >
                    <img
                        src={pest.photo.url}
                        alt={pest.photo.alt || pest.name}
                        className="w-full h-72 object-cover"
                        loading="eager"
                    />
                    <div className="px-5 py-2 text-[11px] text-[var(--vm-text-muted)] bg-[var(--vm-card)]">
                        Photo by{" "}
                        <a
                            href={pest.photo.photographer_url}
                            target="_blank" rel="noreferrer"
                            className="text-[var(--vm-sage)] hover:underline"
                        >
                            {pest.photo.photographer}
                        </a>{" "}
                        on{" "}
                        <a
                            href={pest.photo.pexels_url}
                            target="_blank" rel="noreferrer"
                            className="text-[var(--vm-sage)] hover:underline"
                        >
                            Pexels
                        </a>
                    </div>
                </div>
            )}

            <div className="flex items-start gap-5">
                <div className="text-7xl" aria-hidden>{pest.emoji}</div>
                <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-1">
                        {pest.kind} · {pest.severity}
                    </p>
                    <h1 className="font-serif-display text-5xl text-[var(--vm-text)]" data-testid="pest-detail-name">{pest.name}</h1>
                </div>
            </div>

            <section className="journal-card p-6">
                <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-2">Damage</h2>
                <p className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">{pest.damage}</p>
            </section>

            <section className="grid md:grid-cols-2 gap-4">
                <div className="journal-card p-6">
                    <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-2">Crops affected</h2>
                    <ul className="space-y-1">
                        {pest.crops_affected.map((c) => (
                            <li key={c} className="text-sm text-[var(--vm-text-secondary)] before:content-['·'] before:mr-2 before:text-[var(--vm-sage)]">{c}</li>
                        ))}
                    </ul>
                </div>
                <div className="journal-card p-6">
                    <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-2">Life cycle</h2>
                    <p className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">{pest.life_cycle}</p>
                </div>
            </section>

            <section className="journal-card p-6" data-testid="pest-controls">
                <h2 className="font-serif-display text-xl text-[var(--vm-text)] mb-3">Organic controls</h2>
                <ol className="space-y-2 list-decimal ml-5">
                    {pest.controls.map((c) => (
                        <li key={c} className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">{c}</li>
                    ))}
                </ol>
            </section>

            <div className="journal-card p-5 flex items-center gap-4">
                <MessageSquareText className="w-6 h-6 text-[var(--vm-accent)] shrink-0" />
                <div className="flex-1">
                    <h3 className="font-serif-display text-xl text-[var(--vm-text)]">Need tailored advice?</h3>
                    <p className="text-sm text-[var(--vm-text-muted)]">Ask rooteed about {pest.name} in your garden.</p>
                </div>
                <Button asChild className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                    <Link to={`/chat?prompt=${chatPrompt}`} data-testid="pest-ask-ai-btn">Ask rooteed</Link>
                </Button>
            </div>
        </div>
    );
}
